import type {
  ChannelFeedEngagement,
  ExternalClientApp,
} from "@mlx/contracts";
import Link from "next/link";
import type { ComponentType, ReactNode } from "react";

import {
  ActorHoverView,
  type ActorIdentity,
} from "@/components/app/entities/actor-hover-view";
import {
  ProvenanceAvatarStack,
  type ProvenanceActor,
  type ProvenanceConversation,
  type ProvenanceInterface,
} from "@/components/app/runs/provenance-avatar-stack";
import {
  ContextChipView,
  type ContextChipType,
} from "@/components/app/sessions/context-chip-view";
import { formatClockTime, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  FeedEngagementSummary,
  FeedRowActions,
  type FeedReactions,
} from "./feed-item-engagement";

/*
 * The feed row grammar as markup: an avatar carrying provenance, a sentence
 * that reads "actor verb subject", quiet metadata on the right with the row's
 * own actions beside the time, an optional body, then what the row has
 * gathered. FeedRow and FeedSentence keep the density context, the viewer's
 * reactions and the actor and chip cards, and render through these, so a
 * fixture row composes the same markup. No hooks: a Server Component can
 * draw a row, though reactions carry a handler, so a row that shows them is
 * drawn by a Client Component.
 */

export type FeedRowProvenance = {
  clientApp?: ExternalClientApp | null;
  interfaceType?: ProvenanceInterface | null;
  conversation?: ProvenanceConversation | null;
  executedByTwin?: boolean;
  /** Breathe the Twin mark for this long once the row is on screen. */
  workingSeconds?: number;
  action: "started" | "created" | "updated" | "posted";
};

export type FeedRowTone = "default" | "attention" | "closed" | "static";

/*
 * A surface that shows a few rows among other content (a channel Overview)
 * sets compact rows: a small avatar and tighter spacing that keep to the
 * rhythm of the tables beside them. Feeds keep the default.
 */
export type FeedRowDensity = "default" | "compact";

const TONE_CLASS: Record<FeedRowTone, string> = {
  default: "hover:bg-muted/30",
  attention: "border border-primary/20 bg-primary/5 hover:bg-primary/10",
  closed: "border border-border/30 opacity-60 hover:bg-muted/30",
  /* A row shown as a subject (a thread's root) rather than a stream entry. */
  static: "",
};

export type FeedRowViewProps = {
  actor: ProvenanceActor;
  provenance: FeedRowProvenance;
  occurredAt: number;
  unread?: boolean;
  older?: boolean;
  showConnector?: boolean;
  tone?: FeedRowTone;
  density?: FeedRowDensity;
  sentence: ReactNode;
  body?: ReactNode;
  footer?: ReactNode;
  /** Pass null for a row that can start a thread but has none yet. Omit for a row with no replies. */
  engagement?: ChannelFeedEngagement | null;
  /** The viewer's reactions, which the row's react button and gathered reactions toggle. */
  reactions?: FeedReactions | null;
  replyHref?: string;
  onThreadOpen?: () => void;
  /** Beside the time, before react and reply: a row's own actions. */
  actions?: ReactNode;
};

/** One feed row: the avatar, the sentence with the time and actions to its right, the body, then what it has gathered. */
export function FeedRowView({
  actor,
  provenance,
  occurredAt,
  unread = false,
  older = false,
  showConnector = false,
  tone = "default",
  density = "default",
  sentence,
  body,
  footer,
  engagement,
  reactions = null,
  replyHref,
  onThreadOpen,
  actions,
}: FeedRowViewProps) {
  const compact = density === "compact";
  const engages =
    reactions !== null || engagement !== undefined || replyHref !== undefined;
  return (
    <li
      className={cn(
        "group relative rounded-lg px-2 transition-colors duration-150",
        // A bordered row (a Twin card) keeps room between its text and its edge.
        compact ? (tone === "attention" || tone === "closed" ? "py-2.5" : "py-1.5") : "py-3",
        TONE_CLASS[tone],
      )}
    >
      {showConnector ? (
        <span
          aria-hidden
          className={cn(
            "absolute w-px bg-border/70",
            compact
              ? "bottom-[-6px] left-[17.5px] top-7"
              : "bottom-[-12px] left-[23px] top-10",
          )}
        />
      ) : null}
      <div className={cn("relative flex min-w-0 items-start", compact ? "gap-2.5" : "gap-3")}>
        {/* A compact avatar carries its source as a smaller corner badge. */}
        <ProvenanceAvatarStack
          actor={actor}
          clientApp={provenance.clientApp}
          interfaceType={provenance.interfaceType}
          conversation={provenance.conversation}
          executedByTwin={provenance.executedByTwin}
          workingSeconds={provenance.workingSeconds}
          action={provenance.action}
          size={compact ? "sm" : "md"}
          layout="overlay"
          className={cn("shrink-0 self-start", compact ? "w-5" : "mt-0.5 w-8")}
        />
        <div className="min-w-0 flex-1">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3">
            <div className="min-w-0">{sentence}</div>
            <span className="flex items-center gap-1.5 pt-0.5 text-xs whitespace-nowrap tabular-nums text-muted-foreground">
              {actions}
              {engages ? (
                <FeedRowActions reactions={reactions} href={replyHref} onOpen={onThreadOpen} />
              ) : null}
              {older ? (
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-2xs">
                  Older
                </span>
              ) : null}
              {unread ? (
                <span
                  aria-label="Unread"
                  className="size-1.5 rounded-full bg-primary"
                />
              ) : null}
              <time
                dateTime={new Date(occurredAt).toISOString()}
                title={formatDateTime(occurredAt)}
              >
                {formatClockTime(occurredAt)}
              </time>
            </span>
          </div>
          {body ? <div className="mt-1">{body}</div> : null}
          {footer}
          {engages ? (
            <FeedEngagementSummary
              engagement={(reactions ? reactions.engagement : engagement) ?? null}
              reactions={reactions}
              onOpen={onThreadOpen}
              href={replyHref}
            />
          ) : null}
        </div>
      </div>
    </li>
  );
}

export type FeedSentenceLink = { label: string; href?: string | null };

/** What opens an actor's card from their name; closed, it renders as ActorHoverView. */
export type FeedActorHoverComponent = ComponentType<{
  actor: ActorIdentity;
  className?: string;
  children: ReactNode;
}>;

/** What names a channel or Data Set in a sentence; closed, it renders as ContextChipView. */
export type FeedContextChipComponent = ComponentType<{
  type: ContextChipType;
  label: string;
  href?: string | null;
  className?: string;
}>;

export type FeedSentenceViewProps = {
  actor: string;
  /** Who the actor is; their name then opens their card. */
  actorIdentity?: ActorIdentity | null;
  verb?: string | null;
  subject?: FeedSentenceLink | null;
  status?: string | null;
  /** An updated or archived record's changes, after a colon. */
  changes?: ReactNode;
  /** A created record's field count, after a dot. */
  createdFieldCount?: number;
  dataSet?: FeedSentenceLink | null;
  channel?: FeedSentenceLink | null;
  trailing?: ReactNode;
  /** A header that names the subject renders the sentence as its heading; a
   *  one-line row inside a button renders it as a span. */
  as?: "p" | "h2" | "div" | "span";
  className?: string;
  actorHover?: FeedActorHoverComponent;
  contextChip?: FeedContextChipComponent;
};

/*
 * The sentence every feed row reads as: actor, verb, subject, then status,
 * record changes, the Data Set chip, a created record's field count and the
 * channel chip in that order. A surface inside a channel omits the channel
 * chip; the cross-channel Feed page keeps it.
 */
export function FeedSentenceView({
  actor,
  actorIdentity,
  verb,
  subject,
  status,
  changes,
  createdFieldCount = 0,
  dataSet,
  channel,
  trailing,
  as: Tag = "p",
  className,
  actorHover: ActorName = ActorHoverView,
  contextChip: Chip = ContextChipView,
}: FeedSentenceViewProps) {
  return (
    <Tag
      className={cn(
        "min-w-0 break-words text-sm leading-6 text-muted-foreground",
        className,
      )}
    >
      {actorIdentity ? (
        <ActorName actor={actorIdentity} className="font-medium text-foreground hover:underline hover:underline-offset-4">
          {actor}
        </ActorName>
      ) : (
        <span className="font-medium text-foreground">{actor}</span>
      )}
      {verb ? ` ${verb}` : null}
      {subject ? (
        <>
          {" "}
          <SentenceLink link={subject} />
        </>
      ) : null}
      {status ? (
        <span className="text-tertiary-foreground">{` · ${status}`}</span>
      ) : null}
      {changes ? (
        <>
          {": "}
          {changes}
        </>
      ) : null}
      {dataSet ? (
        <>
          {" · "}
          <Chip
            type="dataSet"
            label={dataSet.label}
            href={dataSet.href}
          />
        </>
      ) : null}
      {createdFieldCount > 0
        ? ` · ${createdFieldCount} ${createdFieldCount === 1 ? "field" : "fields"}`
        : null}
      {channel ? (
        <>
          {" in "}
          <Chip
            type="channel"
            label={channel.label}
            href={channel.href ?? "/workspace/channels"}
          />
        </>
      ) : null}
      {trailing}
    </Tag>
  );
}

function SentenceLink({ link }: { link: FeedSentenceLink }) {
  const className =
    "font-medium text-foreground underline-offset-4 hover:underline";
  return link.href ? (
    <Link href={link.href} className={className}>
      {link.label}
    </Link>
  ) : (
    <span className={className}>{link.label}</span>
  );
}
